import { useEffect, useState, useCallback } from "react";
import { onConsentGranted, onConsentDenied } from "@/lib/analytics";

const CONSENT_KEY = "csm_consent_v1";
const DEV = typeof process !== "undefined" && process.env.NODE_ENV !== "production";

/**
 * useConsent — small hook that owns the analytics consent lifecycle.
 *
 * Responsibilities:
 *   • Read the persisted choice on mount (if any).
 *   • Expose `status` ("granted" | "denied" | "pending") for UI rendering.
 *   • Provide `accept()` / `decline()` that persist the choice and fire
 *     the corresponding analytics hooks.
 *
 * It intentionally does NOT load PostHog. The caller decides whether to
 * instantiate the tracker on accept (see CookieBanner.loadPostHog). That
 * keeps the hook vendor-neutral.
 */
export default function useConsent() {
  const [status, setStatus] = useState("pending");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(CONSENT_KEY);
      if (stored === "granted" || stored === "denied") {
        setStatus(stored);
      }
    } catch (err) {
      if (DEV) {
        // eslint-disable-next-line no-console
        console.warn("useConsent: localStorage read blocked", err);
      }
    }
  }, []);

  const persist = useCallback((value) => {
    try {
      window.localStorage.setItem(CONSENT_KEY, value);
    } catch (err) {
      if (DEV) {
        // eslint-disable-next-line no-console
        console.warn(`useConsent: persist ${value} failed`, err);
      }
    }
  }, []);

  const accept = useCallback(() => {
    persist("granted");
    setStatus("granted");
    onConsentGranted();
  }, [persist]);

  const decline = useCallback(() => {
    persist("denied");
    setStatus("denied");
    onConsentDenied();
  }, [persist]);

  return { status, accept, decline };
}
